/**
 * The main Javascript file for binding the components together to make the system.
 *
 * The Map displays three (3) dynamic layers:
 *     (1) Reference Sites - loaded from the dataset.
 *     (2) Resampled Sites - calculated from the ReferenceSites based on the selected algorithm.
 *     (3) Selected Sites - rendered as a place marker for all selected sites.
 */

// Import for webpack.
import 'bootstrap';
import '../styles/app.scss';

// Make sure JQuery is loaded first.
const $ = global.$ || global.jQuery || require('jquery');

// Load the application config.
const appConfig = require('./app-config.json');

let mapClient = null;

// Imports of libraries/modules used by this module.

// AIMS libraries.
const aimsChart = require('aims-chart');
import { MapClient } from 'aims-map';
import { BasicModel, MapModel } from 'aims-model';

// MSP cost calculator
import {CostCalculatorFactory} from 'msp-cost-calculator';

// Histogram.
import { drawHistogram } from './statistics';

import SelectableSitesFilteringFeatureSelector from './map/selectable-sites-filtering-feature-selector';

// Models.
import DatasetsModel from './models/datasets-model';
import DatasetLoader from './models/dataset-loader';
import VariablesModel from './models/variables-model';
import AlgorithmsModel from './models/algorithms-model';
import Resampler from './models/resampler';
import SiteDistancesCalculatorWorker from 'worker-loader!./models/site-distances-calculator-worker';

import DynamicLayerFeatureStylerImpl from './map/dynamic-layer-feature-styler-impl';
import DynamicLayerMarkerFeatureStylerImpl from './map/dynamic-layer-marker-feature-styler-impl';

import DatasetSelectorComponent from './map/dataset-selector/component';
import VariableSelectorComponent from './map/components/variable-selector';
import AlgorithmSelectorComponent from './map/components/algorithm-selector';
import DynamicLayersLegendBuilder from './map/dynamic-layers-legend-builder';

import HelpDialog from './components/dialogs/help-dialog';
import SelectedSitesPanelComponent from './components/selected-sites-panel/component';
import TripCostsTransformer from "./components/trips-costs/transformer";
import TripsCost from "./components/trips-costs/component";
import CalculationsPanelComponent from "./components/calculations-panel/component";

// Dialogs.
import DownloadCsvDialogLauncher from './components/selected-sites-panel/dialogs/download-csv-dialog-launcher';
import DownloadListDialogLauncher from './components/selected-sites-panel/dialogs/download-list-dialog-launcher';
import ProgressModal from "./components/dialogs/multi-task-progress-modal/component";

// Chart variables.
const CHART_PRIMARY_COLOUR = '#CD5C5C';
const CHART_SECONDARY_COLOUR = '#4169E1';

// Wait for the page to complete loading.
$.when( $.ready )

  // Page has loaded, log a message to say execution is starting.
  .then(() => {
    console.log('Executing...');
  })

  // Initialise the map client.
  .then(() => {

    console.log('Instantiating map client.');
    mapClient = new MapClient('msp-map', appConfig.defaults.map.options);

    // Return the promise that is returned by the init() method of the MapClient.
    return mapClient.init();

  })

  // Load the dataset configs.
  .then(function() {

    console.log('Loading dataset configuration(s).');
    return Promise.all(
      appConfig.datasetConfigs.map((url) => {
        return new Promise((resolve, reject) => {
          console.log(`Loading dataset config: ${url}`);
          $.ajax({
            url: url
          }).done(function (data) {
            resolve(data);
          }).fail(function (jqXHR, textStatus, errorThrown) {
            reject(errorThrown);
          });
        });
      })
    );

  })

  // Map and dataset configs loaded, load/initialise the models and bind to the map and chart.
  .then(function(datasetsConfig) {

    const progressModal = new ProgressModal({title: 'Loading data...'});
    console.log('Starting system.');

    // Capture the olMap reference from the map client.
    const olMap = mapClient.getOlMap();

    // Obtain the necessary APIs from the mapClient.
    const baseLayersAPI = mapClient.getBaseLayersAPI();
    const overlayLayersAPI = mapClient.getOverlayLayersAPI();

    // ---------------------------------------------------------------------------------------------
    //
    // Models
    //
    // ---------------------------------------------------------------------------------------------

    // DatasetsModel is the top-level model, caching the list of available Datasets that can be
    // rendered. Only one Dataset can be active at any time (referred to as the 'activeDataset').
    // When the activeDataset changes, this model fires a {@code VALUE_CHANGED_EVENT} event,
    // and all other models will be cleared/re-populated.
    const datasetsModel = new DatasetsModel(datasetsConfig);

    // VariablesModel caches the list of Variables defined for the activeDataset. The
    // VariableSelectorComponent renders this list and allows the user to select a single
    // 'activeVariable'. The activeVariable only impacts visual components (eg: map and chart).
    const variablesModel = new VariablesModel();
    new VariableSelectorComponent($('#navbar-selectors'), variablesModel);

    // For this application, all sites are unique, so the ReferenceSite data is cached in a hashmap
    // called ReferenceSitesHashModel. If the sites were NOT unique, such as with temporal data,
    // the ReferenceSite data would need to be cached in an Array. ReferenceSite data is immutable
    // and only (re-)loaded when the activeDataset changes. Each site has  a 'latitude' and
    // 'longitude' value, and all variable data is bound to the 'data' object. Note that some
    // variable data may be missing, which will need to be handled as the activeVariable changes.
    const referenceSitesModel = new BasicModel();
    const referenceSitesHashModel = new MapModel();
    const datasetLoader = new DatasetLoader();

    // AlgorithmsModel caches an immutable list of pre-defined resampling algorithms supported by
    // the application. The AlgorithmSelectorComponent renders this list and allows the user to
    // select a single 'activeAlgorithm'.
    const algorithmsModel = new AlgorithmsModel();
    new AlgorithmSelectorComponent($('#navbar-selectors'), algorithmsModel);

    // The SiteDistanceModel is an immutable cache of the distances between sites for use with the
    // AlgorithmModel to determine resampling results. This model is (re-)calculated when the
    // active Dataset changes.
    const siteDistanceModel = new BasicModel();

    // SelectedSitesModel is a subset of the ReferenceSitesHashModel for sites that have been
    // selected by the user. This list is cleared if the underlying ReferenceSitesHashModel changes.
    const selectedSitesModel = new MapModel();

    // ReferenceSitesMapData is the list of sites that are rendered by the Map. This is
    // normally a subset of the ReferenceSitesHashModel after any filtering is applied (such as
    // temporal filtering to show data that occurred during a specific time range). However
    // filtering is not required for this application, so the ReferencesSitesMapDataModel is simply
    // a copy of ReferenceSitesHashModel, and is therefore updated when the ReferenceSitesHashModel
    // is updated, namely when the active Dataset changes.
    const referenceSitesMapDataModel = new MapModel();
    referenceSitesHashModel.on(referenceSitesHashModel.EVENT_DATA_CHANGED, () => {

    });

    // ResampledDataModel is a copy of the sites from ReferenceSitesHashModel, but with their
    // values "resampled" based on the selected sites (SelectedSitesModel) and the algorithm
    // (AlgorithmModel), using the distances between sites (SiteDistanceModel).
    const resampledDataModel = new MapModel();
    const resampler = new Resampler(variablesModel, referenceSitesHashModel, selectedSitesModel,
      algorithmsModel, siteDistanceModel);

    // The ResampledDataModel is updated when a change is made to one of the following:
    // - reference sites (ReferenceSitesHashModel)
    // - selected sites (SelectedSitesModel)
    // - current algorithm (AlgorithmModel).
    referenceSitesHashModel.on(referenceSitesHashModel.EVENT_DATA_CHANGED, () => {
      resampledDataModel.setData(resampler.resample());
    });
    selectedSitesModel.on(selectedSitesModel.EVENT_DATA_CHANGED, () => {
      resampledDataModel.setData(resampler.resample());
    });
    algorithmsModel.on(algorithmsModel.ON_SELECTION_EVENT, (selectedAlgorithm) => {
      resampledDataModel.setData(resampler.resample());
    });

    // ResampledMapDataModel is the list of sites that are rendered by the Map. This is normally a
    // subset of the ResampledDataModel after any filtering is applied (same relationship as
    // between ReferenceSitesHashModel and ReferenceSitesMapModel), however filtering is not
    // required for this application, so the ResampledMapDataModel is simply a copy of the
    // ResampledDataMOdel, and is therefore updated when the ResampledDataModel is updated.
    const resampledMapDataModel = new MapModel();
    resampledDataModel.on(resampledDataModel.EVENT_DATA_CHANGED, () => {
      resampledMapDataModel.setData(resampledDataModel.getData());
    });


    // ---------------------------------------------------------------------------------------------
    //
    // Visual Components
    //
    // ---------------------------------------------------------------------------------------------

    // Legend.
    mapClient.getLegendAPI().registerDynamicLayersLegendBuilder(new DynamicLayersLegendBuilder(variablesModel));

    // Dynamic layers.
    const dynamicLayersAPI = mapClient.getDynamicLayersAPI();

    // Reference sites, re-rendered when either the ReferenceSites or selected variable changes.
    const referenceSitesFeatureStyler = new DynamicLayerFeatureStylerImpl(variablesModel);
    const referenceSitesDynamicLayerRenderer = dynamicLayersAPI.createDynamicLayerRenderer(
      referenceSitesFeatureStyler
    );
    referenceSitesMapDataModel.on(referenceSitesMapDataModel.EVENT_DATA_CHANGED, () => {
      referenceSitesDynamicLayerRenderer.renderLayer(referenceSitesMapDataModel.getData());
    });
    variablesModel.on(variablesModel.ON_SELECTION_EVENT, () => {
      referenceSitesDynamicLayerRenderer.renderLayer(referenceSitesMapDataModel.getData());
    });

    // Resampled sites, re-rendered when either the Resampled values or selected variable changes.
    const resampledDataFeatureStyler = new DynamicLayerFeatureStylerImpl(variablesModel);
    const resampledDataDynamicLayerRenderer = dynamicLayersAPI.createDynamicLayerRenderer(
      resampledDataFeatureStyler
    );
    resampledMapDataModel.on(resampledMapDataModel.EVENT_DATA_CHANGED, () => {
      resampledDataDynamicLayerRenderer.renderLayer(resampledMapDataModel.getData());
    });
    variablesModel.on(variablesModel.ON_SELECTION_EVENT, () => {
      resampledDataDynamicLayerRenderer.renderLayer(resampledMapDataModel.getData());
    });

    // Site selections, re-rendered when the SelectedSitesModel changes.
    const siteSelectionsFeatureStyler = new DynamicLayerMarkerFeatureStylerImpl();
    const siteSelectionsDynamicLayerRenderer = dynamicLayersAPI.createDynamicLayerRenderer(
      siteSelectionsFeatureStyler
    );
    selectedSitesModel.on(selectedSitesModel.EVENT_DATA_CHANGED, () => {
      siteSelectionsDynamicLayerRenderer.renderLayer(selectedSitesModel.getData());
    });

    // Instantiate the panel for displaying, downloading and uploading the list of selected sites.
    new SelectedSitesPanelComponent(
      $('#selected-sites-panel'),
      datasetsModel,
      selectedSitesModel,
      referenceSitesHashModel,
      algorithmsModel,
      variablesModel,
      siteDistanceModel,
      new DownloadListDialogLauncher(
        selectedSitesModel,
      ),
      new DownloadCsvDialogLauncher(
        datasetsModel,
        selectedSitesModel,
        referenceSitesModel,
        referenceSitesHashModel,
        appConfig.downloads.msp.csv
      )
    );

    // Init trips-cost calculator
    const tripsCostCalculator = CostCalculatorFactory.getTripsCalculator();

    referenceSitesHashModel.on(referenceSitesHashModel.EVENT_DATA_CHANGED, function(model) {
      tripsCostCalculator.data = TripCostsTransformer.transformReferenceSites(model.getData());
    });
    const tripsCost = new TripsCost(tripsCostCalculator, $('#trips-cost-wrapper'), olMap, selectedSitesModel);

    // Init calculations feature
    new CalculationsPanelComponent(
      $('#calculations-wrapper'),
      datasetsModel,
      variablesModel,
      resampledDataModel,
      referenceSitesHashModel,
      selectedSitesModel
    );

    // Add support for the slider.
    dynamicLayersAPI.createSlider(
      $('#swipe'),
      referenceSitesDynamicLayerRenderer,
      resampledDataDynamicLayerRenderer
    );

    // Dataset selector.
    const datasetSelectorComponent = new DatasetSelectorComponent(datasetsModel);
    $('.btn-toggle-dataset-selection').click(function() {
      datasetSelectorComponent.toggle();
    });

    // Dynamic feature selector.
    const dynamicLayersFeatureSelector = new SelectableSitesFilteringFeatureSelector(
      olMap,
      referenceSitesDynamicLayerRenderer.getVectorSource(),
      {
        onSelectListeners: [
          (selectedFeatures, addedFeatures, removedFeatures) => {
            selectedSitesModel.setData(selectedFeatures);
          },
          (selectedFeatures, addedFeatures, removedFeatures) => {
            tripsCost.update();
          }
        ]
      },
      selectedSitesModel,
      referenceSitesHashModel
    );

    // Enable/Disable model rendering.
    const showLayersDialogAPI = mapClient.getShowLayersDialogAPI();
    let showSitesState = true;
    showLayersDialogAPI.addToggleOption('Show sites', showSitesState, function() {
      showSitesState = !showSitesState;
      if (showSitesState) {
        referenceSitesDynamicLayerRenderer.show();
        resampledDataDynamicLayerRenderer.show();
        $('#dynamic-layers').show();
        $('#swipe').show();
      } else {
        referenceSitesDynamicLayerRenderer.hide();
        resampledDataDynamicLayerRenderer.hide();
        $('#dynamic-layers').hide();
        $('#swipe').hide();
      }
    });
    let showSelectionsState = true;
    showLayersDialogAPI.addToggleOption('Show selections', showSelectionsState, function() {
      showSelectionsState = !showSelectionsState;
      if (showSelectionsState) {
        siteSelectionsDynamicLayerRenderer.show();
      } else {
        siteSelectionsDynamicLayerRenderer.hide();
      }
    });

    // Re-draw the histogram on events.
    resampledMapDataModel.on(resampledMapDataModel.EVENT_DATA_CHANGED, function() {
      drawHistogram("histogram", "histogramError",
        referenceSitesMapDataModel.getData(), resampledMapDataModel.getData(),
        variablesModel.getSelectedItem());
    });
    variablesModel.on(variablesModel.ON_SELECTION_EVENT, () => {
      drawHistogram("histogram", "histogramError",
        referenceSitesMapDataModel.getData(), resampledMapDataModel.getData(),
        variablesModel.getSelectedItem());
    });

    // Prepare the help dialog.
    const helpDialog = new HelpDialog($('.btn-help'));

    // Clear any selections if the Dataset or Variable changes.
    const clearSelections = function() {
      dynamicLayersFeatureSelector.clear();
    };
    datasetsModel.on(datasetsModel.VALUE_CHANGED_EVENT, clearSelections.bind(this));
    variablesModel.on(variablesModel.ON_SELECTION_EVENT, clearSelections.bind(this));

    // Handle a change of the active Dataset. This invokes a tree of actions that includes
    // refreshing all dependent models and re-rendering the Map and Chart.
    datasetsModel.on(datasetsModel.VALUE_CHANGED_EVENT, function(_activeDataset) {

      // ----------
      // Perform any activities that do not require loading the dataset (ie: the site information).

      // (Re-)populate the VariablesModel.
      variablesModel.load(_activeDataset);

      // Rebuild the styles for the map display.
      referenceSitesFeatureStyler.buildCache();
      resampledDataFeatureStyler.buildCache();

      // ----------
      // Load the dataset (ie: the site information). This is a long running task, so a progress
      // modal is displayed to provide feedback to the user. All long running tasks are added to
      // the progress modal at the start to ensure the modal does not blink as tasks are completed.
      const taskStatusLoadDataset = progressModal.registerTask('loadDataset', 'Load dataset');
      const taskStatusCalculateSiteDistances = progressModal.registerTask('calculateSiteDistances',
        'Calculate site distances');

      // Perform the dataset load.
      datasetLoader.load(_activeDataset)

      // Perform any sequential processing that has insignificant processing requirements.
        .then(siteData => {

          referenceSitesModel.setData(siteData);
          referenceSitesHashModel.setData(siteData);

          // This app does not filter, so set with reference data.
          referenceSitesMapDataModel.setData(siteData);

          // Dataset has changed, so default to reference data.
          resampledDataModel.setData(siteData);

          taskStatusLoadDataset.progress = 100;

        })

        // Perform any parallel processing that has significant processing requirements. These are
        // wrapped in a Promise.all to ensure all complete before the next step is started.
        .then(siteData => {

          // Wrap to control flow.
          return Promise.all(
            [
              // (Re-)calculate the site distances.
              new Promise((resolve, reject) => {

                // Delegate the heavy lifting to a worker (multi-threaded).
                const worker = new SiteDistancesCalculatorWorker();
                worker.postMessage(referenceSitesHashModel.getData());
                worker.onmessage = (event) => {

                  // Update progress.
                  if (event.data.status === 'IN_PROGRESS') {
                    taskStatusCalculateSiteDistances.progress = event.data.progress;
                  }

                  // Handle completion.
                  if (event.data.status === 'COMPLETED') {
                    taskStatusCalculateSiteDistances.progress = 100;
                    siteDistanceModel.setData(event.data.siteDistancesData);
                    worker.terminate();
                    resolve();
                  }
                };

              })

            ])

            // All promises have resolved.
            .then(() => {

              // Pass the site data to anything monitoring this chain of promises.
              return siteData;

            });

        })

        // Perform any follow up actions after the data has been loaded and models refreshed.
        .then(siteData => {

        });

    });

    // Start the system.
    datasetsModel.init();

  });



// WEBPACK FOOTER //
// ./src/app/app.js